import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { IoEyeOutline } from "react-icons/io5";
import "../css/revenuepercreator.css";

const CreatorPerformance = () => {
    const [creators, setCreators] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCreator, setSelectedCreator] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [creatorDetails, setCreatorDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [totalCreators, setTotalCreators] = useState(0);
    const [loadError, setLoadError] = useState(null);
    // Refreshing an already-rendered list, as opposed to the blocking first load
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const itemsPerPage = 10;
    const Api = process.env.REACT_APP_BACKEND_URL;
    const modalStatesRef = useRef({ showDetails });
    const hasLoadedOnceRef = useRef(false);
    const requestIdRef = useRef(0);

    useEffect(() => {
        modalStatesRef.current = { showDetails };
    }, [showDetails]);

    // Debounce the search box before querying the API
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // A new search always starts again from the first page
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    useEffect(() => {
        // Search and paging run on the server so every creator stays reachable
        const skip = (currentPage - 1) * itemsPerPage;
        const baseUrl = Api.endsWith('/') ? Api : `${Api}/`;
        const params = new URLSearchParams({ skip: String(skip), limit: String(itemsPerPage) });
        if (debouncedSearch) params.append('search', debouncedSearch);

        const loadCreators = async (isSilent = false) => {
            const token = localStorage.getItem('access_token');

            // Only the very first load may blank the page. Every later query
            // (typing in the search box, changing page) refreshes the table in
            // place, so the control bar is never unmounted and the search box
            // keeps focus.
            if (!isSilent) {
                if (hasLoadedOnceRef.current) setIsRefreshing(true);
                else setLoading(true);
            }

            // Ignore a response that a newer query has already superseded, so
            // slow replies cannot overwrite what is now in the search box.
            const requestId = ++requestIdRef.current;
            const isCurrent = () => requestId === requestIdRef.current;

            try {
                const response = await fetch(`${baseUrl}admin/stats/creator-performance?${params.toString()}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error(`Failed to load creator performance (${response.status})`);
                const result = await response.json();
                if (!isCurrent()) return;
                const data = result.data || [];
                setCreators(prev => {
                    if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
                    return data;
                });
                setTotalCreators(result.total || 0);
                setLoadError(null);
            } catch (error) {
                console.error('Error loading creator performance:', error);
                if (!isSilent && isCurrent()) {
                    setLoadError('Could not load creator performance. Please try again.');
                }
            } finally {
                if (isCurrent()) {
                    hasLoadedOnceRef.current = true;
                    if (!isSilent) {
                        setLoading(false);
                        setIsRefreshing(false);
                    }
                }
            }
        };

        loadCreators();
        const interval = setInterval(() => {
            if (!modalStatesRef.current.showDetails) {
                loadCreators(true);
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [Api, currentPage, itemsPerPage, debouncedSearch]);

    useEffect(() => {
        if (showDetails) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => { document.body.style.overflow = "unset"; };
    }, [showDetails]);

    const fetchCreatorDetails = async (userId) => {
        setDetailsLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const baseUrl = Api.endsWith('/') ? Api : `${Api}/`;
            const response = await fetch(`${baseUrl}admin/users/${userId}/payment-history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setCreatorDetails(data);
            }
        } catch (error) {
            console.error('Error loading creator details:', error);
        } finally {
            setDetailsLoading(false);
        }
    };

    // The API already applied the search and the page window
    const currentItems = creators || [];
    const totalPages = Math.max(1, Math.ceil(totalCreators / itemsPerPage));
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    if (loading) {
        return (
            <div className="loader-container">
                <div className="theme-loader"></div>
            </div>
        );
    }

    return (
        <div className="revenue-container">
            {/* Control Bar */}
            <div className="control-bar">
                <div className="search-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        className="search-input-field"
                        placeholder="Search by creator name or email..."
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                        }}
                    />
                </div>
            </div>

            {/* Table */}
            <div className={`table-card${isRefreshing ? " is-refreshing" : ""}`}>
                <div className="table-scroll-wrapper">
                    <div className="table-header">
                        <span>S.No</span>
                        <span>Creator Name</span>
                        <span>Followers</span>
                        <span>Posts</span>
                        <span>Views</span>
                        <span>Logo Scans</span>
                        <span>Type</span>
                        <span style={{ textAlign: "center" }}>Action</span>
                    </div>

                    {loadError ? (
                        <div className="empty-state">
                            <div className="empty-state-icon">!</div>
                            <p>{loadError}</p>
                        </div>
                    ) : currentItems.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-icon">$</div>
                            <p>{debouncedSearch ? 'No creators match your search' : 'No creators found'}</p>
                        </div>
                    ) : (
                        currentItems.map((creator, index) => (
                            <div key={creator.user_id} className="table-row">
                                <span>{indexOfFirstItem + index + 1}</span>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                    <img
                                        src={
                                            creator.profile_image
                                                ? `${Api.replace(/\/$/, '')}${creator.profile_image}`
                                                : `https://ui-avatars.com/api/?name=${creator.user_name}&background=ff2d2d&color=fff`
                                        }
                                        alt=""
                                        style={{
                                            width: "32px",
                                            height: "32px",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span style={{ fontWeight: 600 }}>
                                        {creator.user_name || 'Unknown'}
                                    </span>
                                </div>
                                <span style={{ fontWeight: 600 }}>
                                    {creator.followers_count || 0}
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                    {creator.total_posts || 0}
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                    {(creator.total_views || 0).toLocaleString()}
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                    {(creator.logo_scans || 0).toLocaleString()}
                                </span>
                                <span>
                                    <span className={`account-type-badge ${creator.account_type}`}>
                                        {creator.account_type}
                                    </span>
                                </span>
                                <div style={{ display: "flex", justifyContent: "center" }}>
                                    <button
                                        style={{
                                            cursor: "pointer",
                                            background: "transparent",
                                            color: "#6366f1",
                                            border: "none",
                                            padding: "4px",
                                            display: "flex",
                                            alignItems: "center"
                                        }}
                                        onClick={() => {
                                            setSelectedCreator(creator);
                                            setShowDetails(true);
                                            setCreatorDetails(null);
                                            fetchCreatorDetails(creator.user_id);
                                        }}
                                        title="View Details"
                                    >
                                        <IoEyeOutline size={18} />
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Pagination */}
            {totalCreators > itemsPerPage && (
                <div className="pagination-container">
                    <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="pagination-btn"
                    >
                        <FaChevronLeft size={12} />
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            onClick={() => paginate(i + 1)}
                            className={`pagination-btn ${currentPage === i + 1 ? 'active' : ''}`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="pagination-btn"
                    >
                        <FaChevronRight size={12} />
                    </button>
                </div>
            )}

            {/* Details Modal */}
            {showDetails && selectedCreator && (
                <div
                    className="modal-overlay-user"
                    onClick={() => setShowDetails(false)}
                    style={{ zIndex: 300 }}
                >
                    <div
                        className="modal-form-user"
                        style={{ width: "450px" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
                            <h2 className="modal-title-user" style={{ margin: 0 }}>Creator Details</h2>
                            <button
                                onClick={() => setShowDetails(false)}
                                style={{ background: "transparent", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "#888" }}
                            >&times;</button>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                            {/* Creator Info */}
                            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                <img
                                    src={
                                        selectedCreator.profile_image
                                            ? `${Api.replace(/\/$/, '')}${selectedCreator.profile_image}`
                                            : `https://ui-avatars.com/api/?name=${selectedCreator.user_name}&background=ff2d2d&color=fff`
                                    }
                                    alt=""
                                    style={{ width: "55px", height: "55px", borderRadius: "50%", objectFit: "cover", border: "3px solid #ff2d2d" }}
                                />
                                <div>
                                    <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: "700" }}>{selectedCreator.user_name}</h3>
                                    <p style={{ margin: 0, fontSize: "0.85rem", color: "#666" }}>{selectedCreator.email}</p>
                                    <span style={{
                                        fontSize: "0.7rem", fontWeight: "600", textTransform: "uppercase",
                                        color: "#ff2d2d", backgroundColor: "#fff0f0", padding: "2px 8px", borderRadius: "10px"
                                    }}>{selectedCreator.account_type}</span>
                                </div>
                            </div>

                            {/* Engagement Metrics */}
                            <div style={{
                                backgroundColor: "#f8f9fa",
                                padding: "1.5rem",
                                borderRadius: "16px",
                                border: "1px solid #eee"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "1rem" }}>
                                    <span style={{ fontSize: "1.2rem" }}>&#128200;</span>
                                    <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: "700", color: "#1a1a1a" }}>Engagement Metrics</h3>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.2rem" }}>
                                    <div>
                                        <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Followers</label>
                                        <p style={{ margin: "4px 0 0", fontWeight: "700", color: "#ff2d2d", fontSize: "1.1rem" }}>
                                            {selectedCreator.followers_count || 0}
                                        </p>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Posts</label>
                                        <p style={{ margin: "4px 0 0", fontWeight: "700", color: "#1a1a1a", fontSize: "1.1rem" }}>
                                            {selectedCreator.total_posts || 0}
                                        </p>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total Views</label>
                                        <p style={{ margin: "4px 0 0", fontWeight: "600", color: "#1a1a1a" }}>{(selectedCreator.total_views || 0).toLocaleString()}</p>
                                    </div>
                                    <div>
                                        <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px" }}>Logo Scans</label>
                                        <p style={{ margin: "4px 0 0", fontWeight: "600", color: "#1a1a1a" }}>{(selectedCreator.logo_scans || 0).toLocaleString()}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Subscription Info */}
                            {creatorDetails && (
                                <div style={{
                                    backgroundColor: "#f8f9fa",
                                    padding: "1.5rem",
                                    borderRadius: "16px",
                                    border: "1px solid #eee"
                                }}>
                                    <label style={{ fontSize: "0.7rem", color: "#999", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "0.5rem" }}>Subscription</label>
                                    <div style={{ display: "flex", gap: "1.5rem" }}>
                                        <div>
                                            <p style={{ margin: 0, fontSize: "0.85rem", color: "#666" }}>Total Paid</p>
                                            <p style={{ margin: "4px 0 0", fontWeight: "700", color: "#1a1a1a" }}>${creatorDetails.total_paid?.toFixed(2) || "0.00"}</p>
                                        </div>
                                        <div>
                                            <p style={{ margin: 0, fontSize: "0.85rem", color: "#666" }}>Active Plans</p>
                                            <p style={{ margin: "4px 0 0", fontWeight: "700", color: "#1a1a1a" }}>{creatorDetails.active_subscriptions_count || 0}</p>
                                        </div>
                                    </div>
                                    {creatorDetails.subscriptions && creatorDetails.subscriptions.length > 0 && (
                                        <div style={{ marginTop: "1rem" }}>
                                            <p style={{ margin: "0 0 0.5rem", fontSize: "0.85rem", fontWeight: "600" }}>Plans</p>
                                            {creatorDetails.subscriptions.map((sub, idx) => (
                                                <div key={idx} style={{
                                                    display: "flex", justifyContent: "space-between", alignItems: "center",
                                                    padding: "6px 0",
                                                    borderBottom: idx < creatorDetails.subscriptions.length - 1 ? "1px solid #eee" : "none"
                                                }}>
                                                    <span style={{ fontSize: "0.85rem", fontWeight: "500", textTransform: "capitalize" }}>{sub.subscription_type} — {sub.plan}</span>
                                                    <span style={{
                                                        fontSize: "0.7rem", fontWeight: "600",
                                                        color: sub.status === "active" ? "#16a34a" : "#991b1b",
                                                        textTransform: "capitalize"
                                                    }}>{sub.status}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <button
                                onClick={() => setShowDetails(false)}
                                style={{
                                    padding: "14px",
                                    borderRadius: "12px",
                                    backgroundColor: "#000",
                                    color: "#fff",
                                    border: "none",
                                    fontWeight: "700",
                                    cursor: "pointer",
                                    transition: "all 0.2s"
                                }}
                                onMouseOver={(e) => e.target.style.opacity = "0.9"}
                                onMouseOut={(e) => e.target.style.opacity = "1"}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreatorPerformance;
